import {
  InStockFilterValues,
  RangeFilterObjectType,
  SelectFilterObjectType,
} from '@/types';
import React, { useState } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import queryString from 'query-string';

const FILTER_DEFAULT_VALUE = {};

const filterUtils = {
  parseSelectFilters: (
    query: queryString.ParsedQuery,
  ): SelectFilterObjectType => {
    const selectFilters: SelectFilterObjectType = {};

    Object.entries(query).forEach(([key, value]) => {
      const match = key.match(/^filter\[([^\]]+)\]\[\]$/);
      if (match) {
        const code = match[1];
        const values = Array.isArray(value) ? value : [value];
        selectFilters[code] = {
          values: values.map((v) => decodeURIComponent(v as string)),
        };
      }
    });

    return selectFilters;
  },

  parseRangeFilters: (
    query: queryString.ParsedQuery,
  ): RangeFilterObjectType => {
    const rangeFilters: RangeFilterObjectType = {};

    Object.entries(query).forEach(([key, value]) => {
      const minMatch = key.match(/^filter\[([^\]]+)\]\[min\]$/);
      const maxMatch = key.match(/^filter\[([^\]]+)\]\[max\]$/);

      if (minMatch || maxMatch) {
        const code = (minMatch || maxMatch)![1];
        const isMin = !!minMatch;
        const numValue = parseInt(value as string, 10);

        if (!rangeFilters[code]) {
          rangeFilters[code] = { values: { min: 0, max: 0 } };
        }

        if (isMin) {
          rangeFilters[code].values.min = numValue;
        } else {
          rangeFilters[code].values.max = numValue;
        }
      }
    });

    return rangeFilters;
  },

  createSelectParams: (
    filters: SelectFilterObjectType,
  ): Record<string, string[]> => {
    const params: Record<string, string[]> = {};
    Object.entries(filters).forEach(([code, { values }]) => {
      params[`filter[${code}][]`] = values.map((value) =>
        encodeURIComponent(value),
      );
    });
    return params;
  },

  createRangeParams: (
    filters: RangeFilterObjectType,
  ): Record<string, string> => {
    const params: Record<string, string> = {};
    Object.entries(filters).forEach(([code, { values }]) => {
      const { min, max } = values;
      params[`filter[${code}][min]`] = min.toString();
      params[`filter[${code}][max]`] = max.toString();
    });
    return params;
  },

  updateSelectFilter: (
    filters: SelectFilterObjectType,
    code: string,
    value: string,
    shouldRemove = false,
  ): SelectFilterObjectType => {
    const existingValues = filters[code]?.values || [];

    if (shouldRemove || existingValues.includes(value)) {
      const newValues = existingValues.filter((val) => val !== value);
      if (newValues.length === 0) {
        const newFilters = { ...filters };
        delete newFilters[code];
        return newFilters;
      }
      return { ...filters, [code]: { values: newValues } };
    }

    return {
      ...filters,
      [code]: { values: [...existingValues, value] },
    };
  },

  updateRangeFilter: (
    filters: RangeFilterObjectType,
    code: string,
    min: number,
    max: number,
  ): RangeFilterObjectType => {
    if (min === 0 && max === 0) {
      const newFilters = { ...filters };
      delete newFilters[code];
      return newFilters;
    }

    return {
      ...filters,
      [code]: { values: { min, max } },
    };
  },
};

export const useCatalogFilters = () => {
  const pathname = usePathname();
  const { push } = useRouter();
  const searchParams = useSearchParams();

  const query = queryString.parse(searchParams.toString());
  const initialSelectFilters = filterUtils.parseSelectFilters(query);
  const initialRangeFilters = filterUtils.parseRangeFilters(query);
  const initialInStock =
    (query.inStock as InStockFilterValues) || InStockFilterValues.OutOfStock;

  // Инициализируем URL с inStock если его нет
  React.useEffect(() => {
    if (!query.inStock) {
      const currentQuery = queryString.parse(searchParams.toString());
      const queryParams = queryString.stringify(
        {
          ...currentQuery,
          inStock: InStockFilterValues.OutOfStock,
        },
        { skipEmptyString: true, arrayFormat: 'none' },
      );
      push(`${pathname}?${queryParams}`, { scroll: false });
    }
  }, [pathname, push, query.inStock, searchParams]);

  const [appliedSelectFilters, setAppliedSelectFilters] =
    useState<SelectFilterObjectType>(initialSelectFilters);
  const [appliedRangeFilters, setAppliedRangeFilters] =
    useState<RangeFilterObjectType>(initialRangeFilters);

  const [tempSelectFilters, setTempSelectFilters] =
    useState<SelectFilterObjectType>(initialSelectFilters);
  const [tempRangeFilters, setTempRangeFilters] =
    useState<RangeFilterObjectType>(initialRangeFilters);

  const [tempInStock, setTempInStock] =
    useState<InStockFilterValues>(initialInStock);
  const [appliedInStock, setAppliedInStock] =
    useState<InStockFilterValues>(initialInStock);

  const handleSelectChange = (code: string, value: string) => {
    setTempSelectFilters((prev) =>
      filterUtils.updateSelectFilter(prev, code, value),
    );
  };

  const handleRemoveFilter = (code: string, value: string) => {
    const newFilters = filterUtils.updateSelectFilter(
      tempSelectFilters,
      code,
      value,
      true,
    );
    setTempSelectFilters(newFilters);
    setAppliedSelectFilters(newFilters);

    const selectParams = filterUtils.createSelectParams(newFilters);
    const rangeParams = filterUtils.createRangeParams(tempRangeFilters);

    const currentQuery = queryString.parse(searchParams.toString());
    const filteredQuery = Object.fromEntries(
      Object.entries(currentQuery).filter(
        ([key]) => !key.startsWith('filter[') && key !== 'inStock',
      ),
    );

    const queryData = {
      ...filteredQuery,
      ...selectParams,
      ...rangeParams,
    };

    queryData.inStock = appliedInStock;

    const queryParams = queryString.stringify(queryData, {
      skipEmptyString: true,
      arrayFormat: 'none',
    });

    push(`${pathname}${queryParams ? `?${queryParams}` : ''}`);
  };

  const handleRangeChange = (code: string, min: number, max: number) => {
    setTempRangeFilters((prev) =>
      filterUtils.updateRangeFilter(prev, code, min, max),
    );
  };

  const handleApplyFilters = () => {
    setAppliedSelectFilters(tempSelectFilters);
    setAppliedRangeFilters(tempRangeFilters);
    setAppliedInStock(tempInStock);

    const selectParams = filterUtils.createSelectParams(tempSelectFilters);
    const rangeParams = filterUtils.createRangeParams(tempRangeFilters);

    const currentQuery = queryString.parse(searchParams.toString());
    const filteredQuery = Object.fromEntries(
      Object.entries(currentQuery).filter(
        ([key]) => !key.startsWith('filter[') && key !== 'inStock',
      ),
    );

    const queryData = {
      ...filteredQuery,
      ...selectParams,
      ...rangeParams,
    };

    queryData.inStock = tempInStock;

    const queryParams = queryString.stringify(queryData, {
      skipEmptyString: true,
      arrayFormat: 'none',
    });

    push(`${pathname}${queryParams ? `?${queryParams}` : ''}`);
  };

  const handleResetFilters = () => {
    setTempSelectFilters(FILTER_DEFAULT_VALUE);
    setTempRangeFilters(FILTER_DEFAULT_VALUE);
    setAppliedSelectFilters(FILTER_DEFAULT_VALUE);
    setAppliedRangeFilters(FILTER_DEFAULT_VALUE);
    setTempInStock(InStockFilterValues.OutOfStock);
    setAppliedInStock(InStockFilterValues.OutOfStock);

    const currentQuery = queryString.parse(searchParams.toString());
    const filteredQuery = Object.fromEntries(
      Object.entries(currentQuery).filter(
        ([key]) => !key.startsWith('filter[') && key !== 'inStock',
      ),
    );

    const queryParams = queryString.stringify(filteredQuery, {
      skipEmptyString: true,
      arrayFormat: 'none',
    });

    push(`${pathname}${queryParams ? `?${queryParams}` : ''}`);
  };

  const handleChangeStock = (value: InStockFilterValues) => {
    setTempInStock(value);
  };

  return {
    changeSelect: handleSelectChange,
    removeFilter: handleRemoveFilter,
    changeRange: handleRangeChange,
    reset: handleResetFilters,
    apply: handleApplyFilters,
    tempSelects: tempSelectFilters,
    tempRanges: tempRangeFilters,
    appliedSelects: appliedSelectFilters,
    appliedRanges: appliedRangeFilters,
    tempInStock,
    appliedInStock,
    changeStock: handleChangeStock,
  };
};
